import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import TableRowsIcon from "@mui/icons-material/TableRows";
import React from "react";
import { Link } from "react-router-dom";

import type { DatasourceDto } from "../lib/generated/api-client";
import {
  latestFailedAttempt,
  latestSuccessAttempt,
  latestSuccessCollection,
  pickHighlightCollection,
  rankedCollections,
} from "../lib/after-auth";
import { onGuestShareClick } from "../lib/guest-share-click";
import { shareTarget } from "../lib/guest-share";
import { visibleCollections } from "../lib/member-access";
import { useAuth } from "../lib/useAuth";
import { maskConnectionString } from "../lib/mask-connection-string";
import { navDashboards } from "../lib/nav-dashboards";
import { humanize } from "../lib/schema-field";
import { DemoTableTiles } from "./DemoTableTiles";
import { isUserSampleSheet } from "../lib/demo-preference";
import { datasourceDisplayName } from "../lib/sample-label";
import { sheetsReconnectFromAttempt } from "../lib/sheets-reconnect";
import { SheetsReconnectBanner } from "./SheetsReconnectBanner";
import { Button, buttonClassName } from "./ui/Button";
import { Input } from "./ui/Input";

/**
 * `isDemo` / `readOnly` / `viewerRole` exist on the API DTO but not yet in the
 * checked-in generated client, which is regenerated from a running API.
 */
export type Datasource = DatasourceDto & {
  isDemo?: boolean;
  readOnly?: boolean;
  viewerRole?: string;
  /** Slice 2a. `CUSTOM` plus `tabRules` limits which tables this caller sees. */
  access?: "OWNER" | "EDITOR" | "VIEWER" | "CUSTOM";
  tabRules?: { tab: string; level: "NONE" | "VIEW" | "EDIT" }[];
  /** MOSA-146: 'oauth' or 'service_account' for Google Sheets. */
  authMode?: "oauth" | "service_account";
  /** MOSA-146: read above SHEETS_MAX_CELLS, so the grid is read-only. */
  sheetsTooLarge?: boolean;
};

/**
 * An invited teammate: someone with access to the project who does not own it.
 * The API sends the role precisely because a redacted row is otherwise
 * indistinguishable from an owner's unconfigured one.
 */
export const isInvitedMember = (datasource: Datasource): boolean =>
  datasource.viewerRole === "MEMBER";

const TYPE_LABEL: Record<string, string> = {
  POSTGRESQL: "PostgreSQL",
  MYSQL: "MySQL",
  MONGODB: "MongoDB",
  GOOGLE_SHEETS: "Google Sheets",
};

const typeLabel = (type: string) =>
  TYPE_LABEL[type] ?? type.replace(/_/g, " ").toLowerCase();

const logoForType = (type: string) => {
  switch (type) {
    case "POSTGRESQL":
      return "/logos/postgresql.svg";
    case "MONGODB":
      return "/logos/mongodb.svg";
    case "GOOGLE_SHEETS":
      return "/logos/google-sheets.svg";
    case "MYSQL":
      return "/logos/mysql.svg";
    default:
      return "/logos/database.svg";
  }
};

const collectionsOf = (datasource: Datasource) =>
  visibleCollections(
    latestSuccessAttempt(datasource.analyzeAttempts)?.databaseCollections ?? [],
    datasource.access ?? datasource.viewerRole,
    datasource.tabRules,
  );

const dashboardPath = (datasource: Datasource): string | undefined => {
  const name = pickHighlightCollection(collectionsOf(datasource));
  return name
    ? `/datasources/${datasource.id}/collections/${encodeURIComponent(name)}`
    : undefined;
};

/**
 * Where the demo's primary button actually goes, and what to call it.
 *
 * The button said "Open demo dashboard" and opened a table. Every one of the
 * generated demo dashboards is a single-entity twin of a collection the rail
 * already lists, so `navDashboards` drops all five and the dashboard route
 * redirects them: there is no overview page to reach. Rather than keep a label
 * that names a destination the product does not have, the button names the
 * table it opens. If the generator ever emits a dashboard that spans several
 * entities, that page is a real overview and the button says so and goes there.
 */
const demoEntry = (
  datasource: Datasource,
): { path: string; label: string } | undefined => {
  const success = latestSuccessAttempt(datasource.analyzeAttempts);
  const collections = success?.databaseCollections ?? [];
  const generated = (success?.uiGenerationAttempts?.[0]?.panels ?? []).flatMap(
    (panel: { dashboards?: Array<{ id: string; name: string }> }) =>
      panel?.dashboards ?? [],
  );

  const overview = navDashboards(
    generated as any,
    collections
      .map((collection) => collection.name as string)
      .filter(Boolean),
  )[0];

  if (overview?.id) {
    return {
      path: `/datasources/${datasource.id}/dashboards/${overview.id}`,
      label: "Open demo dashboard",
    };
  }

  const name = latestSuccessCollection(datasource.analyzeAttempts);
  return name
    ? {
        path: `/datasources/${datasource.id}/collections/${encodeURIComponent(name)}`,
        label: `Open ${humanize(name)} table`,
      }
    : undefined;
};

const Logo: React.FC<{ type: string }> = ({ type }) => (
  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-od-border bg-od-surface-2 p-1.5">
    <img
      src={logoForType(type)}
      alt=""
      aria-hidden="true"
      className="h-full w-full object-contain"
    />
  </span>
);

const MetaChip: React.FC<{ children: React.ReactNode; title?: string }> = ({
  children,
  title,
}) => (
  <span
    title={title}
    className="inline-flex items-center gap-1 rounded-full border border-od-border bg-od-surface-2 px-2 py-0.5 text-xs text-od-muted"
  >
    {children}
  </span>
);

/* ------------------------------------------------------------- demo card */

/**
 * The API blanks `connectionString` on the shared demo datasource, so the
 * regular card rendered an empty input, an empty action row and a red
 * "Disconnected" dot. The demo gets its own layout: what it is, what is in it,
 * and one way in.
 */
const DemoCard: React.FC<{
  datasource: Datasource;
  onDelete?: () => void;
  analysing?: boolean;
  analyseLabel?: string;
  onAnalyze?: () => void;
}> = ({ datasource, onDelete, analysing = false, analyseLabel, onAnalyze }) => {
  const collections = collectionsOf(datasource);
  const entry = demoEntry(datasource);
  const ranked = rankedCollections(collections);
  const sheetsSample = isUserSampleSheet(datasource);
  const label = datasourceDisplayName(datasource);
  const failed = latestFailedAttempt(datasource.analyzeAttempts);
  // Sheets sample rows are created empty; the app must analyse them. Offer
  // Retry when there are no tables yet (and nothing is already running).
  const needsAnalyse =
    sheetsSample && !analysing && collections.length === 0 && !!onAnalyze;

  const ctaClass =
    "inline-flex shrink-0 items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-semibold transition-opacity hover:opacity-90";
  const ctaStyle = {
    backgroundColor: "var(--od-primary)",
    color: "var(--od-primary-contrast)",
  };

  return (
    <div
      className="rounded-lg border p-4 md:p-6"
      style={{
        borderColor: "color-mix(in srgb, var(--od-primary) 35%, transparent)",
        backgroundColor:
          "color-mix(in srgb, var(--od-primary) 6%, var(--od-surface))",
      }}
      data-testid={sheetsSample ? "sample-sheet-card" : "demo-datasource-card"}
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-start">
        <Logo type={datasource.type} />

        <div className="min-w-0 flex-1">
          <h3 className="m-0 text-base font-semibold text-od-text">{label}</h3>
          <p className="mt-1 mb-0 text-xs text-od-muted">
            {sheetsSample
              ? "A sample Google Sheet we host for you. Browse and filter as usual. Writes stay blocked."
              : "A sample PostgreSQL we host for you. Browse and filter as usual. Writes are simulated and nothing is saved."}
          </p>

          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            <MetaChip>
              <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: "var(--od-success)" }}
              />
              Connected
            </MetaChip>
            <MetaChip>{typeLabel(datasource.type)}</MetaChip>
            <MetaChip>
              <TableRowsIcon sx={{ fontSize: 12 }} />
              {analysing
                ? "Analyzing…"
                : `${collections.length} ${
                    collections.length === 1 ? "table" : "tables"
                  }`}
            </MetaChip>
            <MetaChip title="Writes are not saved on a sample.">
              <LockOutlinedIcon sx={{ fontSize: 12 }} />
              Read-only
            </MetaChip>
            {failed && !analysing && (
              <span
                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium"
                style={{
                  borderColor:
                    "color-mix(in srgb, var(--od-danger) 40%, transparent)",
                  backgroundColor:
                    "color-mix(in srgb, var(--od-danger) 12%, transparent)",
                  color: "var(--od-danger)",
                }}
              >
                <ErrorOutlineIcon sx={{ fontSize: 12 }} />
                Last analysis failed
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-stretch gap-2 md:items-end">
          {entry && (
            <Link
              to={entry.path}
              className={`${ctaClass} hidden md:inline-flex`}
              style={ctaStyle}
            >
              {entry.label} <NorthEastIcon sx={{ fontSize: 14 }} />
            </Link>
          )}
          {needsAnalyse && (
            <Button
              variant="primary"
              size="sm"
              onClick={onAnalyze}
              data-testid="sample-analyze"
            >
              {analyseLabel || (failed ? "Retry analysis" : "Generate pages")}
            </Button>
          )}
          {sheetsSample && onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              data-testid="remove-sample"
            >
              Remove sample
            </Button>
          )}
        </div>
      </div>

      {entry && (
        <Link
          to={entry.path}
          className={`${ctaClass} mt-3 w-full justify-center md:hidden`}
          style={ctaStyle}
        >
          {entry.label} <NorthEastIcon sx={{ fontSize: 14 }} />
        </Link>
      )}

      {sheetsSample && onDelete && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="mt-2 md:hidden"
          data-testid="remove-sample-mobile"
        >
          Remove sample
        </Button>
      )}

      {datasource.id ? (
        <DemoTableTiles
          datasourceId={datasource.id}
          collections={ranked}
          featured={4}
        />
      ) : null}
    </div>
  );
};

/* ---------------------------------------------------------- regular card */

const clipError = (message: string | null | undefined) => {
  const text = (message ?? "").trim();
  if (!text) return "The last analysis did not finish. Click Retry.";
  return text.length > 180 ? `${text.slice(0, 177)}…` : text;
};

const ConnectedCard: React.FC<{
  datasource: Datasource;
  analysing: boolean;
  analyseLabel: string;
  onAnalyze: () => void;
  onDelete: () => void;
}> = ({ datasource, analysing, analyseLabel, onAnalyze, onDelete }) => {
  const collections = collectionsOf(datasource);
  const path = dashboardPath(datasource);
  const connected = !!datasource.connectionString;
  const failed = latestFailedAttempt(datasource.analyzeAttempts);
  const failedMessage = failed ? clipError(failed.errorMessage) : null;
  const reconnect = sheetsReconnectFromAttempt(failed);
  const showReconnect = !!reconnect && !analysing;
  const { guest, user } = useAuth();
  // Same rule as Collection shareHref. Analysis status does not matter:
  // a failed run has no table page, and that page was the only Share link.
  const shareHref = shareTarget({
    id: datasource.id,
    isDemo: datasource.isDemo,
    type: datasource.type,
    projectId: datasource.projectId,
    viewerRole: datasource.viewerRole,
    isGuest: Boolean(guest) && !user,
  });

  return (
    <div
      className="rounded-lg border bg-od-surface p-4"
      style={
        failed && !analysing && !showReconnect
          ? {
              borderColor:
                "color-mix(in srgb, var(--od-danger) 45%, var(--od-border))",
            }
          : { borderColor: "var(--od-border)" }
      }
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-start">
        <Logo type={datasource.type} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {path ? (
              <Link
                to={path}
                className="m-0 inline-flex min-h-11 min-w-11 items-center rounded-sm text-base font-semibold text-od-text hover:text-od-primary-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-od-primary-text focus-visible:ring-offset-2 focus-visible:ring-offset-od-surface sm:min-h-0 sm:min-w-0 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
              >
                {datasource.name}
              </Link>
            ) : (
              <h3 className="m-0 text-base font-semibold text-od-text">
                {datasource.name}
              </h3>
            )}
          </div>

          {reconnect && analysing && (
            <SheetsReconnectBanner info={reconnect} className="mt-2" />
          )}

          {/* PRD Q10. Existing service-account sheets keep working; the owner
              gets a prompt to switch, everyone else sees nothing. */}
          {datasource.type === "GOOGLE_SHEETS" &&
            datasource.authMode === "service_account" &&
            !isInvitedMember(datasource) && (
              <div
                className="mt-2 rounded-md border border-od-border bg-od-surface-2 p-2.5"
                data-testid="sheets-switch-prompt"
              >
                <p className="m-0 text-xs text-od-text">
                  This sheet uses the shared service account. Switch it to
                  Google sign-in so you control access.
                </p>
                <Link
                  to={`/datasources/${datasource.id}`}
                  className="mt-1 inline-flex min-h-11 min-w-11 items-center rounded-sm text-xs font-semibold text-od-primary-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-od-primary-text focus-visible:ring-offset-2 focus-visible:ring-offset-od-surface-2 sm:min-h-0 sm:min-w-0 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
                >
                  Switch to Google sign-in
                </Link>
              </div>
            )}

          {/* ARD section 2: over SHEETS_MAX_CELLS the grid still opens, but
              reads are refused. Say why instead of showing an empty panel. */}
          {datasource.type === "GOOGLE_SHEETS" &&
            datasource.sheetsTooLarge && (
              <p
                className="mt-2 text-xs text-od-warning"
                data-testid="sheets-too-large"
              >
                This sheet is too large to edit in OnlyDash. Split it into
                smaller sheets.
              </p>
            )}

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <MetaChip>
              <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{
                  backgroundColor: connected
                    ? "var(--od-success)"
                    : "var(--od-danger)",
                }}
              />
              {connected ? "Connected" : "Disconnected"}
            </MetaChip>
            <MetaChip>{typeLabel(datasource.type)}</MetaChip>
            {collections.length > 0 ? (
              <MetaChip>
                <TableRowsIcon sx={{ fontSize: 12 }} />
                {collections.length}{" "}
                {collections.length === 1 ? "table" : "tables"}
              </MetaChip>
            ) : failed ? (
              <MetaChip title="A run was started; it did not produce a schema.">
                Analysis failed
              </MetaChip>
            ) : (
              <MetaChip>Not analyzed yet</MetaChip>
            )}
            {failed && !analysing && !showReconnect && (
              <span
                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium"
                style={{
                  borderColor:
                    "color-mix(in srgb, var(--od-danger) 40%, transparent)",
                  backgroundColor:
                    "color-mix(in srgb, var(--od-danger) 12%, transparent)",
                  color: "var(--od-danger)",
                }}
              >
                <ErrorOutlineIcon sx={{ fontSize: 12 }} />
                Last analysis failed
              </span>
            )}
          </div>
          {showReconnect ? (
            <SheetsReconnectBanner info={reconnect} className="mt-2" />
          ) : (
            failed &&
            !analysing && (
              <p className="mt-2 mb-0 text-xs text-od-muted">{failedMessage}</p>
            )
          )}

          <div className="group relative mt-2.5">
            <label
              className="text-xs text-od-muted"
              htmlFor={`connection-${datasource.id}`}
            >
              Connection string
            </label>
            <div className="mt-1 flex gap-2">
            <Input
              id={`connection-${datasource.id}`}
              readOnly
              type="text"
              size="sm" className="flex-1 bg-od-surface-2 font-mono text-xs shadow-none group-hover:border-od-primary/50"
              value={maskConnectionString(datasource.connectionString)}
            />
            <Button
              variant="secondary" size="sm"
              onClick={() =>
                navigator.clipboard.writeText(datasource.connectionString || "")
              }
              title="Copy the original connection string"
            >
              Copy
            </Button>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap gap-2">
          {shareHref && (
            <Link
              to={shareHref}
              className={buttonClassName("secondary", "sm")}
              data-testid="share-panel"
              onClick={() => onGuestShareClick(shareHref)}
            >
              <PersonAddAltIcon sx={{ fontSize: 16 }} /> Share
            </Link>
          )}
          <Button
            disabled={analysing}
            aria-busy={analysing}
            variant="primary" size="sm"
            onClick={onAnalyze}
          >
            {analyseLabel}
          </Button>
          <Button
            aria-label={`Disconnect ${datasource.name}`}
            title="Disconnect"
            variant="secondary" size="icon" className="text-od-muted hover:border-od-danger hover:bg-transparent hover:text-od-danger"
            onClick={onDelete}
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * What an invited teammate sees. The founder handed them a URL so they would
 * stop asking the founder to change rows; the connect form, the analyse button
 * and delete are all owner chrome that they cannot act on. "Disconnected" is
 * additionally a lie: the database is fine, the credential is simply not
 * theirs to see.
 */
const MemberCard: React.FC<{ datasource: Datasource }> = ({ datasource }) => {
  const collections = collectionsOf(datasource);
  const path = dashboardPath(datasource);
  const preview = rankedCollections(collections).slice(0, 6);
  const rest = collections.length - preview.length;

  const ctaClass =
    "inline-flex shrink-0 items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-semibold transition-opacity hover:opacity-90";
  const ctaStyle = {
    backgroundColor: "var(--od-primary)",
    color: "var(--od-primary-contrast)",
  };

  return (
    <div
      className="rounded-lg border bg-od-surface p-4"
      style={{ borderColor: "var(--od-border)" }}
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-start">
        <Logo type={datasource.type} />

        <div className="min-w-0 flex-1">
          <h3 className="m-0 text-base font-semibold text-od-text">
            {datasource.name}
          </h3>
          <p className="mt-1 mb-0 text-xs text-od-muted">
            Shared with you. Browse and edit the tables directly.
          </p>

          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            <MetaChip>{typeLabel(datasource.type)}</MetaChip>
            <MetaChip>
              <TableRowsIcon sx={{ fontSize: 12 }} />
              {collections.length}{" "}
              {collections.length === 1 ? "table" : "tables"}
            </MetaChip>
          </div>
        </div>

        {path && (
          <Link
            to={path}
            className={`${ctaClass} hidden md:inline-flex`}
            style={ctaStyle}
          >
            Open <NorthEastIcon sx={{ fontSize: 14 }} />
          </Link>
        )}
      </div>

      {preview.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-od-border pt-3">
          {preview.map((collection) => (
            <Link
              key={collection.id ?? collection.name}
              to={`/datasources/${datasource.id}/collections/${encodeURIComponent(
                collection.name,
              )}`}
              title={collection.name}
              className="inline-flex min-h-11 min-w-11 items-center rounded-md border border-od-border bg-od-surface px-2 py-0.5 text-xs text-od-muted transition-colors hover:border-od-primary hover:text-od-primary-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-od-primary-text focus-visible:ring-offset-2 focus-visible:ring-offset-od-surface sm:min-h-0 sm:min-w-0 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
            >
              {humanize(collection.name)}
            </Link>
          ))}
          {rest > 0 && (
            <span className="text-xs text-od-muted">+{rest} more</span>
          )}
        </div>
      )}

      {path && (
        <Link
          to={path}
          className={`${ctaClass} mt-3 w-full justify-center md:hidden`}
          style={ctaStyle}
        >
          Open <NorthEastIcon sx={{ fontSize: 14 }} />
        </Link>
      )}
    </div>
  );
};

export const DatasourceCard: React.FC<{
  datasource: Datasource;
  analysing: boolean;
  analyseLabel: string;
  onAnalyze: () => void;
  onDelete: () => void;
}> = (props) =>
  props.datasource.readOnly || props.datasource.isDemo ? (
    <DemoCard
      datasource={props.datasource}
      onDelete={props.onDelete}
      analysing={props.analysing}
      analyseLabel={props.analyseLabel}
      onAnalyze={props.onAnalyze}
    />
  ) : isInvitedMember(props.datasource) ? (
    <MemberCard datasource={props.datasource} />
  ) : (
    <ConnectedCard
      datasource={props.datasource}
      analysing={props.analysing}
      analyseLabel={props.analyseLabel}
      onAnalyze={props.onAnalyze}
      onDelete={props.onDelete}
    />
  );

export default DatasourceCard;
